import { feathers } from '@/feathers-client';

import { generatePaginationModule } from '@/Core/store/modules/pagination';
import { templateTypes as allTemplateTypes } from '../config/template-types';

import { sendEmailDecider } from './templates-helpers';
import { FOLDERS_TAB, TEMPLATES_TAB } from '../../Campaigns/config/constants';

export const DEFAULT_EMAIL_BODY = '<div><br><br><span style="white-space: initial;">{{emailsignature}}</span><br></div>';

const initialState = {
  templatesError: false,
  templatesErrorMessage: '',
  templatesSuccess: false,
  templatesSuccessMessage: '',
  templatesInProgress: false,
  templateFoldersInProgress: false,
  isEditing: false,
  templates: [],
  templateTypes: allTemplateTypes,
  folders: [],
  foldersFilters: [],

  // TODO CS / TODO AK - add filter options here to persist them between page-views

  /**
   * Template viewer-related state
   */
  templateFolders: [],
  displayTemplates: [],
  /**
   * Template composer-related state
   */
  subject: '',
  body: '',
  files: [],
  queries: {
    template_folder_id: 0,
    template_type: '',
    searchValue: '',
  },
  /**
   * Sending options-related state
   */
  isSending: false, // Used for loading animation on sending email/text
  recurrenceRule: '', // recurrenceRule is the RFC5545 standard to record recurrence
  emailSentErrors: [],
  emailsSent: 0,
  emailsScheduled: 0,
  activeTab: TEMPLATES_TAB,
  templateToEdit: {},
  templateToTreeTemplates: {},
  templateToRemove: {},
  showDrawerEdit: false,
  showDrawerTemplatesTree: false,
  showDeleteTemplateDialog: false,
  sharingFolderSuccess: false,
  sharingFolderError: false,
  sharingFolderErrorMessage: '',
  showUnsubscribedError: false,
  hasPhishingError: false,
};

const templatesPagination = generatePaginationModule({
  findAction: 'templates/findTemplates',
});
const templatesFoldersPagination = generatePaginationModule({
  findAction: 'templates/findTemplateFolders',
});

export default {
  namespaced: true,
  modules: {
    pagination: templatesPagination,
    foldersPagination: templatesFoldersPagination,
  },
  state: { ...initialState },

  getters: {
    selectedFolderName(state) {
      const defaultName = 'All folders';
      if (!state.foldersFilters || state.foldersFilters.length === 0) return defaultName;
      const templateFolder = state.foldersFilters.find(folder => {
        return folder.id === state.queries.template_folder_id;
      });
      return templateFolder ? templateFolder.name : defaultName;
    },

    selectedTemplateTypeName(state) {
      const defaultName = 'All types';

      if (!state.templateTypes || state.templateTypes.length === 0) return defaultName;

      const templateType = state.templateTypes.find(type => {
        return type.name === state.queries.template_type;
      });

      return templateType ? templateType.name : defaultName;
    },

    // Checks whether there is data in the subject and body
    isSubjectBodyEmpty(state) {
      return !state.subject && (!state.body || state.body == DEFAULT_EMAIL_BODY);
    },
    queryParams(state, getters) {
      const refinedQueries = {};
      if (state.queries.searchValue) {
        const field = state.activeTab === FOLDERS_TAB ? 'name' : 'subject';
        refinedQueries.$or = [
          { [field]: { $like: `%${state.queries.searchValue}%` } },
          { description: { $like: `%${state.queries.searchValue}%` } },
        ];
      }
      if (state.queries.template_type) {
        refinedQueries.template_type = { $in: state.queries.template_type };
      }
      if (state.queries.template_folder_id && state.activeTab === TEMPLATES_TAB) {
        refinedQueries.template_folder_id = state.queries.template_folder_id;
      }

      const gettersModule =
        state.activeTab === FOLDERS_TAB
          ? 'foldersPagination/queryParams'
          : 'pagination/queryParams';
      return {
        ...getters[gettersModule],
        ...refinedQueries,
      };
    },
    totalAttachmentSizeBytes(state) {
      return state.files.reduce((totalSize, file) => totalSize + file.size_bytes, 0);
    },
    uploadFilesInProgress(state) {
      return state.files.some(file => (file.progress && file.progress < 100) || (!file.progress && !file.created_at && !file.user_id));
    },
  },
  mutations: {
    // Re-initializes the state
    resetState(state) {
      Object.keys(initialState).forEach(field => {
        state[field] = initialState[field];
      });
    },
    // Indicates a HTTP Request is still ongoing
    templatesInProgress(state) {
      state.templatesInProgress = true;
    },
    templatesSearchFinished(state) {
      state.templatesInProgress = false;
    },
    setTemplateFoldersInProgress(state, isInProgress) {
      state.templateFoldersInProgress = isInProgress;
    },
    clearErrors(state) {
      state.templatesError = false;
      state.templatesErrorMessage = '';
    },
    clearSuccess(state) {
      state.templatesSuccess = false;
      state.templatesSuccessMessage = '';
    },
    templatesError(state, { code, message }) {
      state.templatesErrorMessage = `Templates: ${code || ''} ${message}`;
      state.templatesInProgress = false;
      state.templatesError = true;
    },
    templatesSuccess(state, successMessage) {
      state.templatesSuccessMessage = successMessage;
      state.templatesInProgress = false;
      state.templatesSuccess = true;
    },
    setEditing(state, isEditing) {
      state.isEditing = isEditing;
    },
    setSending(state, isSending) {
      state.isSending = isSending;
      if (isSending === false) {
        state.emailsSent = 0;
        state.emailSentErrors = [];
        state.emailsScheduled = 0;
      }
    },
    successfulEmailSent(state) {
      state.emailsSent++;
    },
    setSuccessfulEmailSent(state, qty) {
      state.emailsSent = qty;
    },
    successfulEmailScheduled(state) {
      state.emailsScheduled++;
    },
    setSuccessfulEmailScheduled(state, qty) {
      state.emailsScheduled = qty;
    },
    errorEmailSent(state, contactId) {
      state.emailsSent++;
      state.emailSentErrors = [...state.emailSentErrors, contactId];
    },
    errorContactUnsubscribed(state, shouldShow) {
      state.showUnsubscribedError = shouldShow;
    },
    errorEmailScheduled(state, contactId) {
      state.emailsScheduled++;
      state.emailSentErrors = [...state.emailSentErrors, contactId];
    },
    setSubject(state, subject) {
      state.subject = subject;
    },
    setBody(state, body) {
      state.body = body;
    },
    setFiles(state, files) {
      state.files = files;
    },
    setActiveTab(state, { activeTab }) {
      state.activeTab = activeTab;
    },
    setPhishingError(state, value) {
      state.hasPhishingError = value;
    },

    /**
     * Add or remove templates query parameters.
     *
     * To unset, submit { someQueryParam: false } or any falsey value
     *
     * TODO CS - this file could be moved to a module to handle this type
     *           of query composition.
     *
     * @param   {object}  state
     * @param   {object}  newQuery  the query to add or remove
     */
    addQuery(state, newQuery) {
      Object.keys(newQuery).forEach(property => {
        if (!newQuery[property]) {
          delete state.queries[property];
          delete newQuery[property];
        }
      });

      state.queries = {
        ...state.queries,
        ...newQuery,
      };

      state.queries = Object.assign({}, state.queries);
    },

    // Sets the recurrence rule
    setRecurrenceRule(state, RRule) {
      state.recurrenceRule = RRule;
    },

    setFolders(state, templateFolders) {
      state.folders = templateFolders;
    },

    setFoldersFilters(state, templateFolders) {
      state.foldersFilters = templateFolders;
    },

    // Template viewer-related mutations
    setTemplateFolders(state, templateFolders) {
      templateFolders.forEach(folder => {
        folder.children = [];
      });
      state.templateFolders = JSON.parse(JSON.stringify(templateFolders));
      state.displayTemplates = templateFolders;
    },
    // Accepts templates that matches the search criteria and assigns it to folders
    setSearchTemplates(state, templates) {
      state.templates = Array.isArray(templates) ? templates : templates.data;

      const folders = JSON.parse(JSON.stringify(state.templateFolders));
      templates.forEach(template => {
        template.name = template.description || template.subject;
        const assignedFolder = folders.find(folder => folder.id === template.template_folder_id);
        assignedFolder.children.push(template);
      });
      state.displayTemplates = folders.filter(folder => folder.children.length);
    },

    setTemplates(state, templates) {
      state.templates = templates;
    },

    // Assigns templates to the desired folder
    assignTemplatesToFolder(state, { desiredFolder, templates }) {
      const displayTemplatesClone = JSON.parse(JSON.stringify(state.displayTemplates));
      displayTemplatesClone.forEach(folder => {
        // Finds the correct folder to append the templates
        if (folder.id === desiredFolder.id) {
          let processedTemplates = [];
          if (templates && Array.isArray(templates)) {
            // Sets the display name to be the description of each template
            processedTemplates = templates.map(template => {
              template.name = template.description || template.subject;
              return template;
            });
          }
          folder.children = processedTemplates;
        }
      });
      state.displayTemplates = displayTemplatesClone;
    },
    setTemplateToEdit(state, template) {
      state.templateToEdit = template;
    },
    setShowDrawerEdit(state, isShowDrawer) {
      state.showDrawerEdit = isShowDrawer;
    },
    setShowDrawerTemplatesTree(state, isShow) {
      state.showDrawerTemplatesTree = isShow;
    },
    setShowDeleteTemplateDialog(state, isShow) {
      state.showDeleteTemplateDialog = isShow;
    },
    setTemplateToRemove(state, template) {
      state.templateToRemove = template;
    },
    setTemplateToTreeTemplates(state, template) {
      state.templateToTreeTemplates = template;
    },
    setSharingFolderSuccess(state, boolean) {
      state.sharingFolderSuccess = boolean;
    },
    setSharingFolderErrorMessage(state, { code, message }) {
      state.sharingFolderErrorMessage = `Templates: ${code || ''} ${message}`;
      state.sharingFolderError = true;
    },
    setSharingFolderError(state, boolean) {
      state.sharingFolderError = boolean;
    },
  },
  actions: {
    resetState({ commit }) {
      commit('resetState');
    },
    resetSearch({ commit }) {
      const emptyQuery = { searchValue: null, template_folder_id: null };
      const activeTab = TEMPLATES_TAB;
      commit('addQuery', emptyQuery);
      commit('setActiveTab', { activeTab });
      commit('setTemplateFolders', []);
    },
    clearErrors({ commit }) {
      commit('clearErrors');
    },
    clearSuccess({ commit }) {
      commit('clearSuccess');
    },
    setEditing({ commit }, isEditing) {
      commit('setEditing', isEditing);
    },
    setSubject({ commit }, subject) {
      commit('setSubject', subject);
    },
    setBody({ commit }, body) {
      commit('setBody', body);
    },
    setFiles({ commit }, files) {
      if (files && Array.isArray(files)) {
        commit('setFiles', files);
      }
    },
    setRecurrenceRule({ commit }, payload) {
      commit('setRecurrenceRule', payload);
    },
    resetTemplateToTreeTemplates({ commit }) {
      commit('setTemplateToTreeTemplates', {});
    },
    togglePhishingError({ commit, state }) {
      commit('setPhishingError', !state.hasPhishingError);
    },

    /*
     * Sets the Error state
     */
    async setTemplatesError({ commit }, payload) {
      await commit('clearErrors');
      await commit('clearSuccess');
      commit('templatesError', payload);
    },
    /*
     * Sets the Error state
     */
    async setTemplatesSuccess({ commit }, payload) {
      await commit('clearErrors');
      await commit('clearSuccess');
      commit('templatesSuccess', payload);
    },
    /**
     * @returns {Array} The list of template folders
     * Obtains a list of all template folders.
     */
    async getTemplateFolders({ commit, dispatch }) {
      try {
        commit('setTemplateFoldersInProgress', true);
        const response = await feathers.service('template-folders').find({
          query: { $paginate: 'false', $sort: { name: 1 } },
        });
        return response;
      } catch (error) {
        return dispatch('setTemplatesError', error);
      } finally {
        commit('setTemplateFoldersInProgress', false);
      }
    },
    async findTemplateFolders({ getters, commit, dispatch }) {
      let query = {
        ...getters.queryParams,
        $sort: { name: 1 },
      };
      if (query.$or && query.$or.length) query.$skip = 0;
      try {
        commit('setTemplateFoldersInProgress', true);
        const response = await feathers.service('template-folders').find({ query });
        const folders = response.data;
        commit('setFolders', folders);
        commit('setTemplateFolders', folders);
        commit('foldersPagination/setTotal', response.total);
      } catch (error) {
        return dispatch('setTemplatesError', error);
      } finally {
        commit('setTemplateFoldersInProgress', false);
      }
    },
    async getAllTemplateFolders({ commit, dispatch }, isUpdateTemplateFolders = false) {
      let query = { $paginate: 'false' };
      try {
        commit('setTemplateFoldersInProgress', true);
        const response = await feathers.service('template-folders').find({ query });
        const folders = response;
        commit('setFoldersFilters', folders);
        if (isUpdateTemplateFolders) commit('setTemplateFolders', folders);
      } catch (error) {
        return dispatch('setTemplatesError', error);
      } finally {
        commit('setTemplateFoldersInProgress', false);
      }
    },
    /**
     * @param {Object} templateFolder, The desired template folder to search on
     * @returns {Array} the templates in that folder
     * Calls the feathers backend to obtain the templates based on the current desiredFolder
     */
    async getTemplatesInFolder({ rootState, dispatch }, { templateFolder, type }) {
      try {
        const response = await feathers.service('templates').find({
          query: {
            $paginate: 'false',
            $load: 'template_folder',
            template_type: type,
            template_folder_id: templateFolder.id,
          },
        });
        return response;
      } catch (error) {
        return dispatch('setTemplatesError', error);
      }
    },
    /*
     * Saves a template to the selected folder
     */
    async saveTemplate(
      { commit, dispatch },
      { folder, subject, body, type, description: intitialDescription },
    ) {
      const description = intitialDescription || subject;
      commit('templatesInProgress');

      // Check if a template with the same name already exists
      try {
        await dispatch('checkDuplicateTemplate', description);
      } catch (error) {
        return dispatch('setTemplatesError', error);
      }

      // Call the /templates endpoint to POST a template.
      try {
        const response = await feathers.service('templates').create({
          // In V1 the body of the text is stored in the subject
          // todo bk: resolve this once we move on from V1 database debt
          // subject: body,
          template_folder_id: folder ? folder.id : '',
          template_type: type,
          description,
          subject,
          body,
        });
        // Updates necessary variables and and refreshes the page
        await dispatch(
          'setTemplatesSuccess',
          `Successfully saved a new template ${response.description} in
          folder ${folder ? folder.name : ''}`,
        );
        await commit('setEditing', false);
        return response;
      } catch (error) {
        return dispatch('setTemplatesError', error);
      }
    },
    /**
     * @param {Object} { description, id } The description that we want to check the duplicate of
     * and the id of the current template to exclude it from search matches
     * @returns {Error} Returns an error if one is thrown, otherwise
     *
     * checkDuplicateTemplate checks whether a template of the same name / description exists
     * for any template other than one defined by payload id
     * Since we're ensuring uniqueness on description, we have to make sure one doesn't exist yet.
     */
    async checkDuplicateTemplate({ rootState }, payload) {
      const { description, id } = payload;
      let response;
      try {
        response = await feathers.service('templates').find({
          query: { id: { $ne: id }, description },
        });
      } catch (error) {
        throw error;
      }
      // If there is already a data with the intended description, construct own error object
      if (response.data > 0) {
        throw new Error(`A template with the name "${description}" already exists!`);
      }
    },
    // Sends the email
    async sendEmail({ commit, state, rootState, dispatch }, sendOptions) {
      commit('setSending', true);

      await sendEmailDecider(commit, state, rootState, sendOptions);

      let isSendSuccess = false;

      // Response behaviour
      // in case of a single email being send this will notify the user that there was an error.
      // for bulk actions,  notify the user with an error message if there were mayor errors
      // (more errors than success)
      // Otherwise, show success and the number of emails sent

      if (state.emailsSent === 0 && state.emailsScheduled === 0) {
        dispatch('setTemplatesError', {
          code: '',
          message: 'Unable to send/scheduled Email',
        });
      } else if (state.emailSentErrors.length >= state.emailsSent && state.emailsScheduled === 0) {
        dispatch('setTemplatesError', {
          code: '',
          message: `Only ${state.emailSentErrors.length - state.emailsSent} email(s) were sent`,
        });
      } else {
        let action = 'sent';
        let count = state.emailsSent;
        if (state.emailsScheduled > 0) {
          action = 'scheduled';
          count = state.emailsScheduled;
        }

        if (action === 'scheduled') {
          dispatch('setTemplatesSuccess', `Successfully ${action} email(s)`);
        } else {
          dispatch('setTemplatesSuccess', `Successfully ${action} ${count} email(s)`);
        }
        
        isSendSuccess = true;
      }

      // reset 'sending' status and emailsSent and emailSentErrors counts
      commit('setSending', false);

      return isSendSuccess;
    },

    addQuery({ commit, dispatch, state }, newQuery) {
      commit('addQuery', newQuery);
      state.activeTab === FOLDERS_TAB ? dispatch('findTemplateFolders') : dispatch('findTemplates');
    },

    /**
     * Soft-delete a specific template
     */
    async removeTemplate({ state, commit, getters, dispatch }, templateId) {
      commit('templatesInProgress');

      try {
        const response = await feathers.service('templates').remove(templateId);
        dispatch('findTemplates');
        dispatch('setTemplatesSuccess', `Template "${response.body}" successfully deleted.`);
      } catch (error) {
        dispatch('setTemplatesError', error);
      }
    },

    /**
     * Finds templates based on whichever conditions have been set
     */
    async findTemplates({ state, commit, getters, dispatch }) {
      commit('templatesInProgress');

      const query = {
        $load: 'template_folder',
        ...getters.queryParams,
      };
      try {
        const response = await feathers.service('templates').find({ query });
        commit('setTemplates', response.data);
        commit('pagination/setTotal', response.total);
      } catch (error) {
        dispatch('setTemplatesError', error);
      }
      commit('templatesSearchFinished');
    },

    /**
     * Filter the templates by search string and add assign
     * the desired templates
     *
     * @param {String} searchValue, The desired string to be searched with
     */
    async searchTemplates(
      { rootState, commit, getters, dispatch },
      { searchValue = '', type = '', $load = '' },
    ) {
      commit('templatesInProgress');

      const query = getters.queryParams;
      query.$paginate = false;

      if (searchValue) {
        query.$or = [
          { subject: { $like: `%${searchValue}%` } },
          { description: { $like: `%${searchValue}%` } },
        ];
      }

      if (type) {
        query.template_type = { $in: type };
      }

      if ($load) {
        query.$load = $load;
      }

      try {
        const response = await feathers.service('templates').find({ query });
        commit('setSearchTemplates', response);
        commit('pagination/setTotal', response.length || response.total);
      } catch (error) {
        dispatch('setTemplatesError', error);
      }
      commit('templatesSearchFinished');
    },

    /**
     * @param {String} [name] The desired folder name
     * @returns {Error} Returns an error if one is thrown, otherwise
     * checkDuplicateFolder checks whether a folder of the same name exists.
     * Since we're ensuring uniqueness on name, we have to make sure one doesn't exist yet.
     */
    async checkDuplicateFolder(context, { name, id }) {
      try {
        const response = await feathers.service('template-folders').find({
          query: { name },
        });

        // If there is already a data with the intended description, construct own error object
        // Skip if the existing folder is the same being updated
        if (response.total > 0) {
          const isSameId = id != null ? response.data[0].id === id : false;
          if (!isSameId) {
            throw new Error(`A folder with the name ${name} already exists`);
          }
        }
      } catch (error) {
        throw error;
      }
    },
    /**
     * Function to create a new template folder
     *
     * @param {Object} payload, The input parameters to this function
     * @param {String} payload.name, The desired name of the new template folder
     * @param {String} payload.description, The desired description of the new template folder
     * @returns {Error} The error returns
     */
    async createTemplateFolder({ dispatch }, { name, description }) {
      try {
        await dispatch('checkDuplicateFolder', { name });
      } catch (error) {
        dispatch('setTemplatesError', error);
        return error;
      }
      const query = { name };
      if (description) query.description = description;
      try {
        const response = await feathers.service('template-folders').create(query);
        if (response && response.id) {
          dispatch('setTemplatesSuccess', `Successfully created template folder ${response.name}`);
          return null;
        }
      } catch (error) {
        dispatch('setTemplatesError', error);
        return error;
      }
    },

    /**
     * Function to update a template folder
     *
     * @param {Object} payload, The input parameters to this function
     * @param {String} payload.id, The desired id of the template folder to be updated
     * @param {String} payload.name, The desired name of the template folder to be updated
     * @param {String} payload.description, The desired description of the template folder to be updated
     * @returns {Error} The error returns
     */
    async updateTemplateFolder({ dispatch }, { id, name, description }) {
      try {
        await dispatch('checkDuplicateFolder', { name, id });
      } catch (error) {
        dispatch('setTemplatesError', error);
        return error;
      }
      const query = { name };
      if (description != null) query.description = description;
      try {
        const response = await feathers.service('template-folders').patch(id, query);
        if (response && response.id) {
          dispatch('setTemplatesSuccess', `Successfully updated template folder ${response.name}`);
          return null;
        }
      } catch (error) {
        dispatch('setTemplatesError', error);
        return error;
      }
    },

    setActiveTab({ commit, dispatch }, activeTab) {
      commit('setActiveTab', { activeTab });
      if (activeTab === FOLDERS_TAB) {
        dispatch('findTemplateFolders');
      } else {
        dispatch('findTemplates');
        dispatch('getAllTemplateFolders', true);
      }
    },
    async shareFolderWithTeamMembers({ commit }, { folderId, teamMembersIds }) {
      try {
        const response = await feathers
          .service(`template-folders/${folderId}/share`)
          .create({ teamMembersIdList: teamMembersIds });
        commit('setSharingFolderSuccess', true);
        return null;
      } catch (error) {
        commit('setSharingFolderErrorMessage', error);
        return error;
      }
    },
    setShowUnsubscribedError({ commit }, shouldShow) {
      commit('errorContactUnsubscribed', shouldShow);
    },
  },
};
